import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Component Imports
import Sidebar from './Sidebar';
import Header from './Header';

// Section Imports
import BucketSection from './sections/BucketSection';
import InfluencersSection from './sections/InfluencersSection';
import ClientSection from './sections/ClientSection';
import TestimonialSection from './sections/TestimonialSection';
import UsersSection from './sections/UsersSection';
// import Password from './sections/Password';
import ChangePasswordSection from './sections/ChangePasswordSection';
import SocialSection from './sections/SocialSection';
import SmtpSection from './sections/SmtpSection';
import UserBrandsSection from './sections/UserBrandsSection';
import UserInfluencersSection from './sections/UserInfluencersSection';
import AboutUsSection from './sections/AboutUsSection';
import ServicesSection from './sections/ServicesSection';
import SubBucketsSection from './sections/SubBucketsSection';
import ContactSection from './sections/ContactSection';

interface DashboardProps {
  onLogout: () => void;
}

// 1. Update the Section type to include the new options
type Section =
  | 'bucket'
  | 'influencers'
  | 'client'
  | 'testimonial'
  | 'users'
  | 'userinfluencers'
  | 'userbrands'
  | 'social'
  | 'changePassword'
  | 'smtp'
  | 'about'
  | 'services'
  | 'subbuckets'
  | 'contact';

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [activeSection, setActiveSection] = useState<Section>('bucket');
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default to closed on mobile

  const renderSection = () => {
    if (activeSection === 'subbuckets') {
      return <SubBucketsSection />;
    }

    const sectionVariants = {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 }
    };

    const sections: Record<Section, React.ReactNode> = {
      bucket: <BucketSection />,
      influencers: <InfluencersSection />,
      client: <ClientSection />,
      testimonial: <TestimonialSection />,
      users: <UsersSection />,
      userinfluencers: <UserInfluencersSection />,
      userbrands: <UserBrandsSection />,
      social: <SocialSection />,
      changePassword: <ChangePasswordSection />,
      smtp: <SmtpSection />,
      about: <AboutUsSection />,
      services: <ServicesSection />,
      subbuckets: <SubBucketsSection />,
      contact: <ContactSection />
    };

    const MotionWrapper: React.FC<{ sectionKey: string; children: React.ReactNode }> = ({ sectionKey, children }) => (
      <motion.div
        key={sectionKey}
        variants={sectionVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        {children}
      </motion.div>
    );

    const section = sections[activeSection] || sections['bucket'];
    return (
      <MotionWrapper sectionKey={activeSection}>
        {section}
      </MotionWrapper>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        activeSection={activeSection}
        onSectionChange={(section) => {
          setActiveSection(section);
          if (window.innerWidth < 1024) { // Close sidebar on mobile after selection
            setSidebarOpen(false);
          }
        }}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col transition-all duration-300">
        <Header onLogout={onLogout} onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <AnimatePresence mode="wait">
            {renderSection()}
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;